import { type AgGrid, Card, Flex, Grid, HelpIndicator, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { gridCellStyles } from "../../Common/GridCellBackgrounds/styles_vanilla.css";
import { SourceDocumentCell } from "./SourceDocumentCell";
import { cardHeader, clickableCell, helpIndicator, sectionTitle } from "./styles_vanilla.css";
import { TaxPersonsCountCell } from "./TaxPersonsCountCell";
import { type ControllingPersonEntry, type SourceDocumentInfo } from "./utils";

export type TaxOverviewRowBase = {
  sourceDocument: SourceDocumentInfo | null;
  persons: ControllingPersonEntry[];
};

const DEFAULT_COL_DEF: AgGrid.ColDef = {
  cellStyle: { display: "flex", alignItems: "center" },
};

const GRID_OPTIONS = {
  headerHeight: 48,
  rowHeight: 64,
  autoSizeStrategy: { type: "fitGridWidth" } as const,
  domLayout: "autoHeight" as const,
  suppressCellFocus: true,
};

export const sourceDocumentColumn = <TRow extends TaxOverviewRowBase>(
  caseId: number,
  colId: string,
  headerName: string,
  onAddDocument: (row: TRow) => void,
  readonly = false,
): AgGrid.ColDef<TRow> => ({
  colId,
  headerName,
  minWidth: 400,
  cellClass: (params) => {
    if (params.data?.sourceDocument || readonly) return gridCellStyles.default;
    return `${gridCellStyles.primary} ${clickableCell}`;
  },
  onCellClicked: (params: AgGrid.CellClickedEvent<TRow>) => {
    const row = params.data;
    if (row && !row.sourceDocument && !readonly) {
      onAddDocument(row);
    }
  },
  cellRenderer: (params: AgGrid.ICellRendererParams<TRow>) => {
    const row = params.data;
    if (!row) return null;
    return (
      <SourceDocumentCell
        caseId={caseId}
        sourceDocument={row.sourceDocument}
        onAddDocument={() => onAddDocument(row)}
        readonly={readonly}
      />
    );
  },
});

export const personsColumn = <TRow extends TaxOverviewRowBase>(
  colId: string,
  headerName: string,
  onOpenPersons: (row: TRow) => void,
): AgGrid.ColDef<TRow> => ({
  colId,
  headerName,
  minWidth: 500,
  cellClass: (params) => (params.data && params.data.persons.length > 0 ? clickableCell : ""),
  onCellClicked: (params: AgGrid.CellClickedEvent<TRow>) => {
    const row = params.data;
    if (row && row.persons.length > 0) {
      onOpenPersons(row);
    }
  },
  cellRenderer: (params: AgGrid.ICellRendererParams<TRow>) => {
    const row = params.data;
    if (!row) return null;
    return <TaxPersonsCountCell persons={row.persons} onClick={() => onOpenPersons(row)} />;
  },
});

export type TaxOverviewSectionProps<TRow extends TaxOverviewRowBase> = {
  title: string;
  helpText?: string;
  rows: TRow[];
  columnDefs: AgGrid.ColDef<TRow>[];
};

export const TaxOverviewSection = <TRow extends TaxOverviewRowBase>({
  title,
  helpText,
  rows,
  columnDefs,
}: TaxOverviewSectionProps<TRow>) => (
  <Card>
    <Flex className={cardHeader} alignItems="center" gap={Sizes.XS}>
      <Text variant="secondary-md" className={sectionTitle}>
        {title}
      </Text>
      {helpText && (
        <HelpIndicator
          id={`${title}-help`}
          placement="right"
          content={helpText}
          className={helpIndicator}
          fullWidth
        >
          <Icon type="info-circle" variant="outline" color={Colors.Neutral600} />
        </HelpIndicator>
      )}
    </Flex>
    <Grid<TRow>
      columnDefs={columnDefs}
      rowData={rows}
      gridOptions={GRID_OPTIONS}
      defaultColDef={DEFAULT_COL_DEF}
      reactiveCustomComponents
    />
  </Card>
);
